import {
  LayoutDashboard,
  AlertCircle,
  Settings,
  Shield,
  ChevronUp,
  User,
  LogOut,
  CreditCard,
  ScanSearch,
  Plus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

// Menu items with their corresponding feature flags
const menuItems = [
  {
    title: "Dashboard",
    url: "/security",
    icon: LayoutDashboard,
    featureFlag: "security_dashboard" as const,
  },
  {
    title: "Issues",
    url: "/issues",
    icon: AlertCircle,
    featureFlag: "issues" as const,
  },
  {
    title: "Alerts & Policies",
    url: "/alerts",
    icon: Shield,
    featureFlag: "alerts" as const,
  },
  {
    title: "New Scan",
    url: "/compliance",
    icon: Plus,
    featureFlag: "demo_flow" as const,
  },
  {
    title: "AI Compliance Scans",
    url: "/llm-scans",
    icon: ScanSearch,
    featureFlag: "llm_scans" as const,
  },
];

/**
 * Renders the application sidebar with feature-flag filtered navigation and a user menu that provides profile links and logout.
 *
 * The navigation list is filtered by feature flags: while flags are loading all menu items are shown; after flags load only items with a true feature flag are included; if feature flags are unavailable no navigation items are shown. The user menu displays the current user's name and email and includes links to Profile, Billing, Settings, and a Log out action that returns the user to /login.
 *
 * @returns The sidebar React element.
 */
export function AppSidebar() {
  const { user, logout, isLoading } = useAuth0();
  const { data: featureFlags, isLoading: flagsLoading } = useFeatureFlags();

  // Filter menu items based on feature flags

  const items = featureFlags
    ? menuItems.filter((item) => featureFlags[item.featureFlag] === true)
    : flagsLoading
      ? menuItems
      : [];
  // Get user display name and email from Auth0
  const userName = user?.name || user?.nickname || "User";
  const userEmail = user?.email || "user@example.com";

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin + "/login",
      },
    });
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/" className="flex items-center gap-2" data-tour="sidebar-logo">
                <img
                  src="/logo.png"
                  alt="EarlyCore Logo"
                  className="size-8 rounded-lg object-contain"
                />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">EarlyCore</span>
                  <span className="truncate text-xs text-muted-foreground">Platform</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu data-tour="sidebar-nav">
              {items.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    data-tour={item.url === "/compliance" ? "compliance-menu" : undefined}
                  >
                    <Link to={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  disabled={isLoading}
                >
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground">
                    <User className="size-4" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{userName}</span>
                    <span className="truncate text-xs text-muted-foreground">{userEmail}</span>
                  </div>
                  <ChevronUp className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" className="w-[--radix-popper-anchor-width] mb-2">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/billing" className="cursor-pointer">
                    <CreditCard className="mr-2 h-4 w-4" />
                    <span>Billing</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
